<template>
    <div>
        <AdminPageHeader title="Categorías del catálogo">
            <template #description>
                <p class="adm-muted categorias__lead">
                    Agrupan los productos en el catálogo público (<code>/print3d/catalogo</code>).
                    No confundir con las categorías de <strong>tipo de impresión</strong>, que se
                    administran desde <router-link to="/admin/print3d/materiales">Materiales</router-link>
                    y sólo afectan al costeo.
                </p>
            </template>
        </AdminPageHeader>

        <div class="adm-grid-2">
            <AdmCard :title="selected ? `Editando categoría #${selected.id}` : 'Nueva categoría'">
                <form class="categorias__form" @submit.prevent="onSubmit">
                    <AdmField
                        v-model="form.name"
                        label="Nombre"
                        required
                        maxlength="80"
                        :error="errors.name"
                    />

                    <AdmField
                        v-model="form.slug"
                        label="Identificador en la URL"
                        maxlength="60"
                        placeholder="Se genera del nombre si lo dejas vacío"
                        hint="Se usa en /print3d/catalogo?categoria=… Cambiarlo rompe los enlaces ya compartidos."
                    />

                    <AdmField
                        v-model="form.description"
                        label="Descripción"
                        type="textarea"
                        :rows="2"
                        maxlength="300"
                    />

                    <AdmField
                        v-model="form.displayOrder"
                        label="Orden de visualización"
                        type="number"
                        :step="1"
                        :min="0"
                    />

                    <label v-if="selected" class="categorias__checkbox">
                        <input v-model="form.isActive" type="checkbox" /> Activa
                    </label>

                    <div class="categorias__actions">
                        <AdmButton type="submit" :loading="saving">
                            {{ selected ? 'Guardar cambios' : 'Crear categoría' }}
                        </AdmButton>
                        <AdmButton variant="ghost" @click="resetSelection">Cancelar</AdmButton>
                    </div>
                </form>

                <template v-if="selected" #footer>
                    <AdmButton variant="danger" :icon="Trash2" @click="onDelete(selected)">
                        Desactivar categoría
                    </AdmButton>
                </template>
            </AdmCard>

            <AdmCard title="Categorías registradas" flush>
                <template #actions>
                    <label class="categorias__checkbox">
                        <input v-model="includeInactive" type="checkbox" @change="fetchItems" />
                        Mostrar inactivas
                    </label>
                </template>

                <AdmTable
                    :columns="columns"
                    :rows="items"
                    :selected-id="selected?.id ?? null"
                    empty-message="Todavía no hay categorías. Crea la primera para poder clasificar los productos del catálogo."
                >
                    <template #cell-name="{ row }">
                        {{ row.name }}
                        <AdmBadge v-if="!row.isActive" tone="neutral" label="Inactiva" />
                    </template>

                    <template #cell-slug="{ value }">
                        <code>{{ value }}</code>
                    </template>

                    <template #cell-actions="{ row }">
                        <AdmButton variant="secondary" size="sm" :icon="Pencil" @click="select(row)">
                            Editar
                        </AdmButton>
                    </template>
                </AdmTable>
            </AdmCard>
        </div>
    </div>
</template>

<script>
/**
 * Categorías del catálogo 3D — migrada en el plan 27_09_2026 (F3/T21).
 *
 * Se retiran la barra `#menu` duplicada y la segunda definición propia de
 * `.data-table`. La validación del nombre pasa de `alert()` a error en el
 * propio campo, que es donde el operario lo necesita.
 */
import { Trash2, Pencil } from 'lucide-vue-next';
import {
    getProductCategories, saveProductCategory, editProductCategory, deleteProductCategory,
} from '@/services/dbPrint3dAdmin.js';
import AdminPageHeader from '@/components/Admin/AdminPageHeader.vue';
import AdmCard from '@/components/Admin/ui/AdmCard.vue';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import AdmTable from '@/components/Admin/ui/AdmTable.vue';
import AdmBadge from '@/components/Admin/ui/AdmBadge.vue';
import { notify } from '@/composables/useAdmToast.js';

const emptyForm = () => ({
    name: '',
    slug: '',
    description: '',
    displayOrder: 0,
    isActive: true,
});

export default {
    name: 'Print3DCategoriasPage',
    components: { AdminPageHeader, AdmCard, AdmField, AdmButton, AdmTable, AdmBadge },
    data() {
        return {
            items: [],
            selected: null,
            includeInactive: false,
            form: emptyForm(),
            errors: { name: '' },
            saving: false,
            columns: [
                { key: 'id', label: '#', width: '60px' },
                { key: 'name', label: 'Nombre' },
                { key: 'slug', label: 'URL' },
                { key: 'productCount', label: 'Productos', align: 'right', width: '100px' },
                { key: 'displayOrder', label: 'Orden', align: 'right', width: '80px' },
                { key: 'actions', label: 'Acciones', align: 'right', width: '110px' },
            ],
            Trash2, Pencil,
        };
    },
    mounted() { this.fetchItems(); },
    methods: {
        async fetchItems() {
            const { categories, error } = await getProductCategories(this.includeInactive);
            if (error) {
                notify.error(`Error al cargar categorías: ${error}`);
                return;
            }
            this.items = categories;
        },
        select(category) {
            this.selected = { ...category };
            this.errors.name = '';
            this.form = {
                name: category.name,
                slug: category.slug,
                description: category.description || '',
                displayOrder: category.displayOrder,
                isActive: category.isActive,
            };
        },
        resetSelection() {
            this.selected = null;
            this.errors.name = '';
            this.form = emptyForm();
        },
        async onSubmit() {
            this.errors.name = '';
            if (!this.form.name.trim()) {
                this.errors.name = 'El nombre es obligatorio.';
                return;
            }
            const payload = { ...this.form, name: this.form.name.trim() };
            // El slug vacío deja que el backend lo derive del nombre; al editar
            // no se envía para no reescribir el que ya está publicado.
            if (!payload.slug) delete payload.slug;

            this.saving = true;
            const { error } = this.selected
                ? await editProductCategory(this.selected.id, payload)
                : await saveProductCategory(payload);
            this.saving = false;

            if (error) {
                notify.error(`${this.selected ? 'Error al editar' : 'Error al crear'}: ${error}`);
                return;
            }
            notify.success(this.selected ? 'Categoría actualizada.' : 'Categoría creada.');
            this.resetSelection();
            this.fetchItems();
        },
        async onDelete(category) {
            const aviso = category.productCount
                ? `Esta categoría agrupa ${category.productCount} producto(s). Al desactivarla dejarán de mostrarse agrupados en el catálogo público (siguen visibles como "Sin categoría"). ¿Continuar?`
                : '¿Desactivar esta categoría?';
            if (!confirm(aviso)) return;
            const { error } = await deleteProductCategory(category.id);
            if (error) {
                notify.error(`Error al desactivar: ${error}`);
                return;
            }
            notify.success('Categoría desactivada.');
            this.resetSelection();
            this.fetchItems();
        },
    },
};
</script>

<style scoped>
.categorias__lead { margin: 0.3rem 0 0 0; max-width: 70ch; }

.categorias__form {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap);
}

.categorias__checkbox {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: var(--adm-font-sm);
    color: var(--color-text);
}

.categorias__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--adm-gap-sm);
}
</style>
